/* eslint-disable no-param-reassign */
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import FlagsSearchForm, {
  FormFields as FlagsSearchFormFields
} from 'components/forms/FlagsSearchForm/FlagsSearchForm';

import ClientForm, {
  FormFields as ClientFormFormFields
} from '../ClientSearch';
import ServiceProyects, {
  FormFields as ServiceProyectsFormFields
} from '../ServiceSearch';
import LocationSearch, {
  FormFields as LocationSearchFormFields
} from '../UbicationSearch';
import WorkForceSearch, {
  FormFields as WorkForceSearchFormFields
} from '../WorkForceSearch';
import ConfigComponentSearch, {
  FormFields as ConfigComponentSearchFormFields
} from '../ConfigurationComponentSearch';
import IncidentOrReq, {
  FormFields as IncidentOrReqFormFields
} from '../IncidentOrReqSearch';
import TicketsSearching, {
  FormFields as TicketsSearchingFormFields
} from '../TicketSearch';
import DatePickerSearcher, {
  FormFields as DatePickerSearcherFormFields
} from '../DatePickerSearch';
import SubFormAccordion from '../SubFormAccordion';
import ServiceTypeSearch, {
  FormFields as ServiceTypeSearchFormFields
} from '../ServiceTypeSearch';
import AlteredSearchTickets from '../AlertedSearch';

type Props = {
  form: UseFormReturn<FormFields>;
};

export type FormFields = ClientFormFormFields &
  ServiceProyectsFormFields &
  LocationSearchFormFields &
  WorkForceSearchFormFields &
  ConfigComponentSearchFormFields &
  IncidentOrReqFormFields &
  TicketsSearchingFormFields &
  DatePickerSearcherFormFields &
  FlagsSearchFormFields &
  ServiceTypeSearchFormFields & { partnerIds?: number[] } & {
    externalEventId?: number;
    externalOrigin?: string;
  };

/**
 * Convierte una fecha a formato UTC ISO string.
 *
 * Transforma una fecha local a formato UTC para envío a la API.
 *
 * @param {string | Date} date - Fecha a convertir.
 * @returns {string | undefined} Fecha en formato UTC o undefined si no hay fecha.
 *
 * @example
 * convertFormatToUTC('2023-12-01T10:30:00') // '2023-12-01T10:30:00'
 */
export const convertFormatToUTC = (date: string | Date) =>
  date ? dayjs(date).utc().format('YYYY-MM-DDTHH:mm:ss') : undefined;

/**
 * Componente formulario de filtros para búsqueda avanzada de tickets.
 *
 * Proporciona múltiples acordeones con filtros categorizados para refinar
 * la búsqueda de tickets por cliente, servicio, ubicación, fuerza de trabajo, etc.
 *
 * @param {object} props - Props del componente.
 * @param {UseFormReturn<FormFields>} props.form - Instancia del formulario para manejar los filtros.
 *
 * @returns {JSX.Element} Formulario de filtros organizados en acordeones.
 *
 * @example
 * <FilterForm form={formInstance} />
 */
function FilterForm({ form }: Props) {
  const { t } = useTranslation('global');

  return (
    <Grid xs={12} item>
      <Box sx={{ overflowY: 'auto' }}>
        <SubFormAccordion id="client" title={t('ticket.messages.clientData')}>
          <ClientForm form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="service"
          title={t('common.commonProperties.serviceSingular')}
        >
          <ServiceProyects form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="location"
          title={t('common.commonProperties.ubication')}
        >
          <LocationSearch form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="force"
          title={t('common.commonProperties.taskForce')}
        >
          <WorkForceSearch form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="component"
          title={
            <>
              {t('common.commonProperties.configurationComponent')}{' '}
              <small>{`(${t('common.commonProperties.device')})`}</small>
            </>
          }
        >
          <ConfigComponentSearch form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="request"
          title={t('common.commonProperties.incidentOrRequirement')}
        >
          <IncidentOrReq form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="request"
          title={t('common.commonProperties.followUp')}
        >
          <FlagsSearchForm form={form} />
        </SubFormAccordion>

        <SubFormAccordion id="tickets" title="Ticket">
          <TicketsSearching form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="dates"
          title={`${t('common.commonProperties.date')}(s)`}
        >
          <DatePickerSearcher form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="serviceType"
          title={t('common.commonProperties.serviceType')}
        >
          <ServiceTypeSearch form={form} />
        </SubFormAccordion>

        <SubFormAccordion
          id="externalTicket"
          title={t('common.commonProperties.alerted')}
        >
          <AlteredSearchTickets form={form} />
        </SubFormAccordion>
      </Box>
    </Grid>
  );
}

export default FilterForm;
